/**
 * Stocks search hooks — directApiClient-backed
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import { TickerSearchResult } from './types';

interface UseStocksParams {
  searchQuery: string;
}

interface UseStocksReturn {
  data: TickerSearchResult[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

async function fetchStocks(searchQuery: string): Promise<TickerSearchResult[]> {
  return directApiClient.get<TickerSearchResult[]>('/stocks/search', {
    params: { q: searchQuery }
  });
}

/**
 * Below the 2-character search minimum the picker shows a DEFAULT SHORTLIST rather
 * than nothing (VALE-1031): the shares most often selected across matters, which the
 * backend returns for `/stocks/search` with no `q`.
 *
 * Collapsing 0 and 1 characters onto the same empty query is deliberate. It gives
 * both the same react-query key, so typing the first character — and deleting it
 * again — reuses the cached shortlist instead of blanking the list and refetching.
 */
export function useStocks({ searchQuery }: UseStocksParams): UseStocksReturn {
  const q = searchQuery.length >= 2 ? searchQuery : '';

  const query = useQuery<TickerSearchResult[], Error>({
    queryKey: ['stocks', q],
    queryFn: () => fetchStocks(q),
    staleTime: 30 * 1000
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ?? null
  };
}

export type { TickerSearchResult };
